/**
 * Admin panel requests (F9).
 *
 * Thin wrappers over the admin API: taxonomy CRUD, scorecard weights and
 * thresholds, user management, and demo data load/clear.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 7.13, 7.14, PRD §5, F9
 */
import type {
  AdminSettings,
  AdminUser,
  CreateTaxonomyRequest,
  ListTaxonomyQuery,
  SaveBandThresholdsRequest,
  SaveScorecardCriteriaRequest,
  SaveStallThresholdsRequest,
  TaxonomyTerm,
  UpdateTaxonomyRequest,
} from '@nexus/contracts';

import { api } from '../../../lib/api-client';

// ── Taxonomy ────────────────────────────────────────────────────────────────

export async function listTaxonomy(query: ListTaxonomyQuery): Promise<TaxonomyTerm[]> {
  return api.get<TaxonomyTerm[]>('/admin/taxonomy', { query });
}

export async function createTaxonomy(body: CreateTaxonomyRequest): Promise<TaxonomyTerm> {
  return api.post<TaxonomyTerm>('/admin/taxonomy', body);
}

export async function updateTaxonomy(
  id: string,
  body: UpdateTaxonomyRequest,
): Promise<TaxonomyTerm> {
  return api.patch<TaxonomyTerm>(`/admin/taxonomy/${id}`, body);
}

export async function deleteTaxonomy(id: string): Promise<void> {
  /* The endpoint returns 204 with no body; the client resolves to undefined,
     which is what a `Promise<void>` wants. */
  await api.delete(`/admin/taxonomy/${id}`);
}

// ── Settings ────────────────────────────────────────────────────────────────

export async function getSettings(): Promise<AdminSettings> {
  return api.get<AdminSettings>('/admin/settings');
}

export async function saveScorecardCriteria(
  body: SaveScorecardCriteriaRequest,
): Promise<AdminSettings> {
  return api.put<AdminSettings>('/admin/settings/scorecard-criteria', body);
}

export async function saveBandThresholds(
  body: SaveBandThresholdsRequest,
): Promise<AdminSettings> {
  return api.put<AdminSettings>('/admin/settings/bands', body);
}

export async function saveStallThresholds(
  body: SaveStallThresholdsRequest,
): Promise<AdminSettings> {
  return api.put<AdminSettings>('/admin/settings/stall-thresholds', body);
}

// ── Users ─────────────────────────────────────────────────────────────────────

export async function listUsers(): Promise<AdminUser[]> {
  return api.get<AdminUser[]>('/admin/users');
}

export async function changeUserRole(id: string, role: string): Promise<AdminUser> {
  return api.patch<AdminUser>(`/admin/users/${id}/role`, { role });
}

export async function setUserActive(id: string, isActive: boolean): Promise<AdminUser> {
  return api.patch<AdminUser>(`/admin/users/${id}/active`, { isActive });
}

// ── Demo data ───────────────────────────────────────────────────────────────

export async function loadDemoData(): Promise<{ affected: number }> {
  return api.post<{ affected: number }>('/admin/demo/load');
}

export async function clearDemoData(): Promise<{ affected: number }> {
  return api.post<{ affected: number }>('/admin/demo/clear');
}
